import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { BUILDINGS } from "@/data/content";
import { Reveal } from "@/components/site/Reveal";

const Hotspot = ({ building, active, onToggle }) => {
  const { lang, t } = useLanguage();
  return (
    <div
      className="absolute z-10"
      style={{ left: `${building.hotspot.x}%`, top: `${building.hotspot.y}%` }}
    >
      <button
        data-testid={`map-hotspot-${building.slug}`}
        onClick={(e) => {
          e.stopPropagation();
          onToggle(building.slug);
        }}
        aria-label={building.name[lang]}
        className="relative flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center"
      >
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-lagoon/30" />
        <span
          className={`relative inline-flex h-4 w-4 rounded-full border-2 border-white shadow-[0_2px_10px_rgba(10,17,24,0.35)] transition-colors duration-300 ${
            active ? "bg-nature" : "bg-lagoon"
          }`}
        />
      </button>
      <AnimatePresence>
        {active && (
          <motion.div
            data-testid={`map-tooltip-${building.slug}`}
            initial={{ opacity: 0, y: 10, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="absolute bottom-full left-1/2 z-20 mb-3 w-52 -translate-x-1/2 border border-ink/5 bg-white/95 p-4 shadow-[0_24px_60px_-20px_rgba(10,17,24,0.35)] backdrop-blur-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="font-serif text-xs italic text-lagoon">{building.num}</p>
            <p className="mt-1 font-serif text-xl leading-tight text-ink">
              {building.name[lang]}
            </p>
            <Link
              to={`/batiment/${building.slug}`}
              data-testid={`map-tooltip-link-${building.slug}`}
              className="group mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-lagoon transition-colors duration-300 hover:text-nature"
            >
              {t.map.discover}
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const InteractiveMap = () => {
  const { lang, t } = useLanguage();
  const [active, setActive] = useState(null);

  return (
    <>
      <Reveal delay={0.15}>
        <div
          data-testid="interactive-map"
          onClick={() => setActive(null)}
          className="relative aspect-[4/3] w-full overflow-hidden border border-neutral-200"
        >
          <img
            src="/images/carte-aerienne.webp"
            alt={t.map.placeholder}
            data-testid="img-carte-aerienne"
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
          />
          {BUILDINGS.map((b) => (
            <Hotspot
              key={b.slug}
              building={b}
              active={active === b.slug}
              onToggle={(slug) => setActive((v) => (v === slug ? null : slug))}
            />
          ))}
        </div>
      </Reveal>

      <div className="mt-16 md:mt-24">
        <Reveal>
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-ink/40">
            {t.map.legend}
          </p>
        </Reveal>
        <div className="mt-6 grid gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2 lg:grid-cols-5">
          {BUILDINGS.map((b, i) => (
            <Reveal key={b.slug} delay={0.06 * i}>
              <Link
                to={`/batiment/${b.slug}`}
                data-testid={`map-legend-${b.slug}`}
                className="group flex h-full flex-col justify-between gap-8 bg-white p-6 transition-colors duration-300 hover:bg-sand"
              >
                <span className="font-serif text-sm italic text-lagoon">{b.num}</span>
                <span className="flex items-center justify-between gap-2">
                  <span className="font-serif text-xl leading-tight text-ink">
                    {b.name[lang]}
                  </span>
                  <ArrowUpRight className="h-4 w-4 shrink-0 text-ink/30 transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-lagoon" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </>
  );
};
